import { createColumnHelper, type ColumnDef } from '@tanstack/react-table'
import { createContext, useContext } from 'react'
import { Link } from 'react-router'
import { match } from 'ts-pattern'
import { pillFromRun, runProgressOf, verifiedFindingsOf } from '../../components/beacon/beacon-run.ts'
import { BeaconStartButton } from '../../components/beacon/beacon-scan-button.tsx'
import { BeaconScanningPill } from '../../components/beacon/beacon-scanning-pill.tsx'
import { columnWidth, type RepositoriesTableLayout } from '../../components/tables/repositories-table.columns.tsx'
import { Routes } from '../../routes.ts'
import type { BeaconRun, Repository } from '../../utils/api-client/api-schemas.ts'
import { pluralize } from '../../utils/text-utils.ts'
import * as styles from './beacon.columns.css.ts'

/**
 * The latest Beacon run of every listed repository, fetched once for the whole page. The cells read it
 * from here rather than each asking for its own row, so a page of a hundred repositories is one request.
 */
export type BeaconRunsLookup =
  { status: 'loading' } | { status: 'error' } | { status: 'ready'; runs: ReadonlyMap<string, BeaconRun> }

export const BeaconRunsContext = createContext<BeaconRunsLookup>({ status: 'loading' })

const columnHelper = createColumnHelper<Repository>()

/**
 * The PIXEE BEACON column. Created once per layout: the runs arrive through {@link BeaconRunsContext}, so
 * a refreshed run re-renders its cell without rebuilding the table's columns.
 */
export const createBeaconColumnDefinition = (layout: RepositoriesTableLayout): ColumnDef<Repository> =>
  columnHelper.display({
    id: 'beacon',
    meta: { width: columnWidth(layout, 'beacon') },
    header: () => <span className={styles.header}>PIXEE BEACON</span>,
    cell: ({ row }) => <BeaconCell repositoryId={row.original.id} />,
  })

export const BeaconCell: React.FC<{ repositoryId: string }> = ({ repositoryId }) => {
  const lookup = useContext(BeaconRunsContext)
  return match(lookup)
    .with({ status: 'ready' }, ({ runs }) => (
      <BeaconCellPure repositoryId={repositoryId} run={runs.get(repositoryId)} />
    ))
    .with({ status: 'loading' }, () => <span className={styles.unknown}>Loading…</span>)
    .with({ status: 'error' }, () => (
      <span className={styles.unknown} title="The latest Beacon runs could not be loaded.">
        Unavailable
      </span>
    ))
    .exhaustive()
}

export type BeaconCellPureProps = { repositoryId: string; run?: BeaconRun }

const formatExamined = (run: BeaconRun) => {
  const examinedPercent = runProgressOf(run)?.examined_percent
  return examinedPercent === undefined ? undefined : `${Math.round(examinedPercent)}% examined`
}

/**
 * One repository's Beacon state, every state leading to its run: the run in flight as a pill, a completed
 * run's result, or — never scanned, or the last run stopped early — the button that starts one.
 */
export const BeaconCellPure: React.FC<BeaconCellPureProps> = ({ repositoryId, run }) => {
  if (!run) {
    return (
      <div className={styles.cell}>
        <BeaconStartButton repositoryId={repositoryId} buttonType="secondary" size="compact" />
      </div>
    )
  }
  const runPath = Routes.BeaconRunPage.createPath({ runId: run.id })
  return match(run.state)
    .with('queued', 'dispatching', 'in-progress', () => (
      <div className={styles.cell}>
        <BeaconScanningPill pill={pillFromRun(run)} to={runPath} size="compact" />
      </div>
    ))
    .with('completed', () => {
      const findings = verifiedFindingsOf(run) ?? 0
      const examined = formatExamined(run)
      return (
        <div className={styles.cell}>
          <span className={styles.result}>
            {findings} {pluralize(findings, 'finding', 'findings')}
            {examined && <span className={styles.examined}> · {examined}</span>}
          </span>
          <Link
            to={runPath}
            className={styles.view}
            aria-label={`View the Beacon run: ${findings} ${pluralize(findings, 'finding', 'findings')}`}
          >
            View
          </Link>
        </div>
      )
    })
    .with('failed', 'cancelled', state => (
      <div className={styles.cell}>
        <Link to={runPath} className={styles.stopped}>
          Last run {state}
        </Link>
        <BeaconStartButton repositoryId={repositoryId} buttonType="secondary" size="compact" />
      </div>
    ))
    .exhaustive()
}
